import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Plus, Pencil, Trash2, X, CreditCard, GripVertical, Check } from 'lucide-react';
import ToggleSwitch from '../components/common/ToggleSwitch';
import {
  ACCESS_CONTROLS, DEFAULT_PACKAGES, PACKAGE_ACCENTS, ACCENT_KEYS, CARD_STYLES,
  isControlIncluded, BILLING_PERIODS,
} from '../data/accessControls';
import {
  getSubscriptionPackages, createSubscriptionPackage,
  updateSubscriptionPackage, deleteSubscriptionPackage, reorderSubscriptionPackages,
} from '../redux/subscriptionSlice';
import PackageCardView from '../components/subscription/PackageCardView';

const emptyForm = () => ({
  id: null, name: '', price: '', accent: 'teal', style: 'bar', popular: false, badgeText: 'Most Popular', controls: {}, durationDays: 30, billingPeriods: {}, purchasable: true,
});
const accentOf = (key) => PACKAGE_ACCENTS[key] || PACKAGE_ACCENTS.teal;

// Square checkbox used to pick plan features (replaces the toggle switches).
const FeatureCheckbox = ({ checked, disabled = false, onClick, testId }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={testId}
    aria-checked={checked}
    role="checkbox"
    className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
      checked ? 'bg-[#4ABDBD] border-[#4ABDBD]' : 'bg-white border-gray-300'
    } ${disabled ? 'opacity-70 cursor-default' : 'cursor-pointer hover:border-[#4ABDBD]'}`}
  >
    {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
  </button>
);

// ---- A single admin package card — full design via the shared PackageCardView,
// with the admin €X/mo price + Edit/Delete actions. ----
const PackageCard = ({ pkg, onEdit, onDelete, namePadStart = false }) => {
  const a = accentOf(pkg.accent);
  const hasPrice = pkg.price !== '' && pkg.price != null;
  const isCustom = pkg.purchasable === false;

  const renderPrice = (onAccent) => (
    <div className="flex items-baseline gap-1 mt-1">
      {isCustom ? (
        <span className={`text-xl font-black ${onAccent ? 'text-white' : a.text}`}>Custom pricing</span>
      ) : hasPrice ? (
        <>
          <span className={`text-3xl font-black ${onAccent ? 'text-white' : a.text}`}>€{pkg.price}</span>
          <span className={`text-xs font-medium ${onAccent ? 'text-white/80' : 'text-slate-400'}`}>/mo</span>
        </>
      ) : (
        <span className={`text-xl font-bold italic ${onAccent ? 'text-white' : a.text}`}>On request</span>
      )}
    </div>
  );

  return (
    <PackageCardView
      pkg={pkg}
      namePadStart={namePadStart}
      cardTestId={`subscription-package-${pkg.id}`}
      nameTestId={`subscription-package-name-${pkg.id}`}
      popularTestId={`subscription-popular-badge-${pkg.id}`}
      emptyText="No access controls assigned"
      renderPrice={renderPrice}
      footer={
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`subscription-edit-${pkg.id}`}
            onClick={() => onEdit(pkg)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-[#4ABDBD] bg-[#4ABDBD]/10 hover:bg-[#4ABDBD]/20 font-semibold text-sm transition-colors"
          >
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button
            type="button"
            data-testid={`subscription-delete-${pkg.id}`}
            onClick={() => onDelete(pkg)}
            title="Delete"
            className="p-2 rounded-xl text-red-500 bg-red-50 hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      }
    />
  );
};

// Admin package manager. CRUD persists to the backend; falls back to local seed
// defaults for display when the backend isn't reachable yet.
const Subscription = () => {
  const dispatch = useDispatch();
  const { packages, saving } = useSelector((s) => s.subscription);
  const [form, setForm] = useState(null);
  const [ready, setReady] = useState(false);
  const [ordered, setOrdered] = useState([]);
  const dragFrom = useRef(null);

  // Fetch once and only reveal the grid after it resolves, so the seed-default
  // cards never flash before the real packages arrive.
  useEffect(() => {
    Promise.resolve(dispatch(getSubscriptionPackages())).finally(() => setReady(true));
  }, [dispatch]);

  // Mirror the store order locally so drag can reorder optimistically.
  useEffect(() => { setOrdered(packages); }, [packages]);

  // Real (backend) packages have UUID ids and are draggable + reorderable; the
  // seed fallback is shown only before the backend is reachable.
  const isReal = packages.length > 0;
  const displayPackages = isReal
    ? (ordered.length === packages.length ? ordered : packages)
    : DEFAULT_PACKAGES;

  const onDragStart = (i) => { dragFrom.current = i; };
  const onDragEnter = (i) => {
    if (dragFrom.current === null || dragFrom.current === i) return;
    setOrdered((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragFrom.current, 1);
      next.splice(i, 0, moved);
      dragFrom.current = i;
      return next;
    });
  };
  const onDragEnd = () => {
    if (dragFrom.current === null) return;
    dragFrom.current = null;
    dispatch(reorderSubscriptionPackages(ordered.map((p) => p.id)))
      .unwrap()
      .catch(() => { setOrdered(packages); toast.warn('Could not save the new order'); });
  };

  const openAdd = () => setForm(emptyForm());
  const openEdit = (pkg) => setForm({ badgeText: 'Most Popular', style: 'bar', ...pkg, controls: { ...pkg.controls } });
  const close = () => setForm(null);

  // A control is "on" in the FORM when its key is present and not false — including a
  // value control whose value is still blank (e.g. Patient limit right after ticking
  // it), so the checkbox stays checked and its value input keeps showing while the
  // admin types. isControlIncluded() treats '' as not-included and is only for
  // display/enforcement, not the form's checkbox state.
  const ctrlOn = (controls, id) => controls?.[id] !== undefined && controls?.[id] !== false;

  const toggleControl = (id) => {
    const ctrl = ACCESS_CONTROLS.find((c) => c.id === id);
    setForm((f) => {
      const controls = { ...f.controls };
      if (ctrlOn(controls, id)) delete controls[id];
      // Enabling a value control: options → default to the last (lowest) option so
      // it registers as included; free-text → blank (the input appears to be typed in).
      else controls[id] = ctrl.type === 'value' ? (ctrl.options ? ctrl.options[ctrl.options.length - 1] : '') : true;
      return { ...f, controls };
    });
  };
  const setControlValue = (id, value) => setForm((f) => ({ ...f, controls: { ...f.controls, [id]: value } }));

  // Billing periods: toggle a period on/off and set its price.
  const togglePeriod = (pid) => setForm((f) => {
    const bp = { ...(f.billingPeriods || {}) };
    const cur = bp[pid] || { enabled: false, price: '' };
    bp[pid] = { ...cur, enabled: !cur.enabled };
    return { ...f, billingPeriods: bp };
  });
  const setPeriodPrice = (pid, price) => setForm((f) => {
    const bp = { ...(f.billingPeriods || {}) };
    bp[pid] = { ...(bp[pid] || { enabled: true }), price };
    return { ...f, billingPeriods: bp };
  });

  const canSave = form && form.name.trim().length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    const payload = {
      name: form.name.trim(), price: String(form.price).trim(), accent: form.accent,
      style: form.style, popular: form.popular, badgeText: form.badgeText, controls: form.controls,
      durationDays: Number(form.durationDays) || 30,
      billingPeriods: form.billingPeriods || {},
      purchasable: form.purchasable !== false,
    };
    try {
      // Real backend ids are UUIDs; the seed-fallback ids ('free'…) aren't editable.
      if (form.id && String(form.id).includes('-')) {
        await dispatch(updateSubscriptionPackage({ id: form.id, ...payload })).unwrap();
        toast.success('Package updated');
      } else {
        await dispatch(createSubscriptionPackage(payload)).unwrap();
        toast.success('Package created');
      }
      close();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not save package — is the backend deployed?');
    }
  };

  const remove = async (pkg) => {
    const res = await Swal.fire({
      title: 'Delete package?',
      text: `Remove "${pkg.name}" permanently?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#9ca3af',
      confirmButtonText: 'Delete',
    });
    if (!res.isConfirmed) return;
    try {
      await dispatch(deleteSubscriptionPackage(pkg.id)).unwrap();
      toast.success('Package deleted');
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not delete — is the backend deployed?');
    }
  };

  return (
    <div className="min-h-full w-full p-2">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8">
        <div className="flex items-center gap-3">
          <span className="p-2.5 rounded-2xl bg-[#4ABDBD]/10 text-[#4ABDBD] shadow-sm">
            <CreditCard className="w-6 h-6 sm:w-7 sm:h-7" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="subscription-title">Subscription</h1>
            <p className="text-sm text-slate-500">Create packages and assign access controls.</p>
          </div>
        </div>
        <button
          type="button"
          data-testid="subscription-add-package"
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all"
        >
          <Plus className="w-4 h-4" /> Add package
        </button>
      </div>

      {!ready ? (
        <div className="flex justify-center py-16" data-testid="subscription-loading">
          <div className="h-8 w-8 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {isReal && (
            <p className="text-xs text-slate-400 mb-3" data-testid="subscription-reorder-hint">
              Drag a card by its handle to reorder — this order is shown to doctors too.
            </p>
          )}

          {/* Package cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 items-stretch" data-testid="subscription-package-list">
            {displayPackages.map((pkg, i) => (
              <div
                key={pkg.id}
                onDragEnter={() => onDragEnter(i)}
                onDragOver={(e) => e.preventDefault()}
                className="relative"
                data-testid={`subscription-package-wrap-${pkg.id}`}
              >
                {isReal && (
                  <div
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragEnd={onDragEnd}
                    title="Drag to reorder"
                    data-testid={`subscription-drag-handle-${pkg.id}`}
                    className="absolute top-2 left-2 z-20 p-1 rounded-md bg-white/80 backdrop-blur text-gray-400 hover:text-[#4ABDBD] shadow-sm cursor-grab active:cursor-grabbing"
                  >
                    <GripVertical className="w-4 h-4" />
                  </div>
                )}
                <PackageCard pkg={pkg} namePadStart={isReal} onEdit={openEdit} onDelete={remove} />
              </div>
            ))}

            <button
              type="button"
              onClick={openAdd}
              data-testid="subscription-add-card"
              className="rounded-2xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-[#4ABDBD] hover:text-[#4ABDBD] transition-colors flex flex-col items-center justify-center gap-2 p-5 min-h-[220px]"
            >
              <Plus className="w-8 h-8" />
              <span className="text-sm font-semibold">Add package</span>
            </button>
          </div>
        </>
      )}

      {/* Add / Edit modal */}
      {form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="subscription-form-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#1a365d]">{form.id ? 'Edit package' : 'New package'}</h2>
              <button type="button" onClick={close} className="p-2 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              {/* Plan type — purchasable (self-serve) vs custom (contact only) */}
              <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[#1a365d]">Doctors can subscribe online</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {form.purchasable !== false
                      ? 'Standard plan — doctors pay and subscribe themselves.'
                      : 'Custom plan — shown to everyone, but doctors send a contact request instead of paying.'}
                  </p>
                </div>
                <ToggleSwitch
                  id="form-purchasable"
                  on={form.purchasable !== false}
                  onToggle={() => setForm((f) => ({ ...f, purchasable: f.purchasable === false }))}
                />
              </div>

              {/* Name + price */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Package name</label>
                  <input
                    data-testid="subscription-form-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Premium"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Price (€ / month)</label>
                  <input
                    data-testid="subscription-form-price"
                    value={form.price}
                    onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    placeholder="blank = On request"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Trial / default length (days)</label>
                  <input
                    type="number"
                    min="1"
                    data-testid="subscription-form-duration"
                    value={form.durationDays ?? 30}
                    onChange={(e) => setForm((f) => ({ ...f, durationDays: e.target.value }))}
                    placeholder="e.g. 30 — Free = trial length"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                  />
                </div>
              </div>

              {/* Billing periods (paid plans) — admin enables + prices each */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Billing periods &amp; pricing (paid plans)</label>
                <div className="space-y-2">
                  {BILLING_PERIODS.map((p) => {
                    const cur = (form.billingPeriods || {})[p.id] || { enabled: false, price: '' };
                    return (
                      <div key={p.id} className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => togglePeriod(p.id)}
                          data-testid={`period-toggle-${p.id}`}
                          className={`w-28 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${cur.enabled ? 'bg-[#4ABDBD]/10 border-[#4ABDBD] text-[#2D8A8A]' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                        >
                          {cur.enabled ? '✓ ' : ''}{p.label}
                        </button>
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                          <input
                            type="number"
                            min="0"
                            disabled={!cur.enabled}
                            value={cur.price}
                            onChange={(e) => setPeriodPrice(p.id, e.target.value)}
                            data-testid={`period-price-${p.id}`}
                            placeholder={`${p.days}-day price`}
                            className={`w-full pl-7 pr-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${cur.enabled ? 'border-gray-300' : 'border-gray-200 bg-gray-50 text-gray-400'}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-2">Enable the periods this plan offers and set each price; the doctor picks one at checkout. Leave all off for the Free / trial plan.</p>
              </div>

              {/* Card style */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Card design</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CARD_STYLES.map((s) => {
                    const sel = form.style === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`subscription-form-style-${s.id}`}
                        onClick={() => setForm((f) => ({ ...f, style: s.id }))}
                        className={`text-xs font-semibold px-2 py-2 rounded-xl border transition ${
                          sel ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]' : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Card accent */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Card accent</label>
                <div className="flex flex-wrap gap-2">
                  {ACCENT_KEYS.map((key) => {
                    const a = PACKAGE_ACCENTS[key];
                    const sel = form.accent === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        title={a.label}
                        data-testid={`subscription-form-accent-${key}`}
                        onClick={() => setForm((f) => ({ ...f, accent: key }))}
                        className={`w-8 h-8 rounded-full bg-gradient-to-br ${a.bar} transition-transform hover:scale-110 ${
                          sel ? 'ring-2 ring-offset-2 ring-[#1a365d]' : 'ring-1 ring-black/5'
                        }`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Most popular */}
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Most popular</p>
                    <p className="text-xs text-slate-400">Show a highlight badge on this package.</p>
                  </div>
                  <ToggleSwitch id="form-popular" on={form.popular} onToggle={() => setForm((f) => ({ ...f, popular: !f.popular }))} />
                </div>
                {form.popular && (
                  <input
                    data-testid="subscription-form-badge-text"
                    value={form.badgeText}
                    onChange={(e) => setForm((f) => ({ ...f, badgeText: e.target.value }))}
                    placeholder="Most Popular"
                    className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                  />
                )}
              </div>

              {/* Control features — checkboxes the admin selects per plan. */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Control features</p>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {ACCESS_CONTROLS.filter((ctrl) => ctrl.section === 'control').map((ctrl) => {
                    const included = ctrlOn(form.controls, ctrl.id);
                    return (
                      <div key={ctrl.id} className="flex items-center gap-3 px-3 py-2.5">
                        <FeatureCheckbox testId={`form-${ctrl.id}`} checked={included} onClick={() => toggleControl(ctrl.id)} />
                        <span className="flex-grow min-w-0 text-sm font-medium text-gray-700">{ctrl.label}</span>
                        {ctrl.type === 'value' && included && (
                          ctrl.options ? (
                            <select
                              data-testid={`subscription-form-value-${ctrl.id}`}
                              value={typeof form.controls[ctrl.id] === 'string' ? form.controls[ctrl.id] : ''}
                              onChange={(e) => setControlValue(ctrl.id, e.target.value)}
                              className="w-36 px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            >
                              {ctrl.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          ) : (
                            <input
                              data-testid={`subscription-form-value-${ctrl.id}`}
                              value={typeof form.controls[ctrl.id] === 'string' ? form.controls[ctrl.id] : ''}
                              onChange={(e) => setControlValue(ctrl.id, e.target.value)}
                              placeholder={ctrl.placeholder}
                              className="w-36 px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Static features — display-only labels the admin can pick per plan.
                  They appear on the plan card but don't enforce anything in the app. */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Static features</p>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {ACCESS_CONTROLS.filter((ctrl) => ctrl.section === 'static').map((ctrl) => {
                    const included = isControlIncluded(form.controls, ctrl.id);
                    return (
                      <div key={ctrl.id} className="flex items-center gap-3 px-3 py-2.5">
                        <FeatureCheckbox testId={`form-static-${ctrl.id}`} checked={included} onClick={() => toggleControl(ctrl.id)} />
                        <span className="flex-grow min-w-0 text-sm font-medium text-gray-700">{ctrl.label}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Static features are shown on the plan card for display only — picking them doesn&apos;t change how the app works.</p>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3">
              <button type="button" onClick={close} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors">
                Cancel
              </button>
              <button
                type="button"
                data-testid="subscription-form-save"
                onClick={save}
                disabled={!canSave}
                className={`px-4 py-2 rounded-xl text-white font-semibold text-sm transition-all ${
                  canSave ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg hover:scale-105' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving…' : form.id ? 'Save changes' : 'Create package'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
